import {
  Children,
  cloneElement,
  isValidElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import valid from 'card-validator';
import { Button, FormCheckbox, TermsConditions, useCheckoutDisplayAmount, useSafeSelector, InputViewTwo, formatAmountCentsBasedOnCurrency, getTermsAndConditionsURL } from '@unified-checkout/kit-components';
import { CardNumber } from '../../components/CardNumber/CardNumberViewTwo';
import { ExpiryDate } from '../../components/ExpiryDate/ExpiryDate';
import { CVV } from '../../components/CVV/CVV';
import { CardHolder } from '../../components/CardHolder/CardHolder';
import {
  CARD_TYPES,
  AMEX_CARD_LENGTH,
  CARD_NUMBER_SPACES,
  CARD_LENGTH,
  EXPIRY_DATE_LENGTH,
  AMEX_CVV_LENGTH,
  CVV_LENGTH,
  CARD_HOLDER_NAME_MAX_LENGTH,
  SAVED_CARD_TYPES,
} from '../../constants';
import InstantRefundOpt from '../../components/InstantRefundOpt';
import InfoIcon from '../../assets/icons/Vector.png';
import { CardToInstallment, InstallmentContext, INSTALLMENT_BIN_LENGTH } from './cardToInstallment';
// import { useSelector } from 'react-redux';

export function Card(props: { [key: string]: any; children?: ReactNode }) {
  const {
    onSubmit,
    loading,
    customStyle,
    setIsOmanNetCard,
    useHookForm,
    children,
    getCardLogo,
    setIsValidVisa,
    showSaveCard,
    showTerms,
    onDiscount,
    onFees,
    discountAppliedMessage,
    discountErrorMessage,
    hasOmanNetIntegration,
    forceSaveCard,
    content,
    hideCardIcons,
    buttonLabel,
    splitPayments,
    currency,
    CardPaymentWrapper,
    amount,
    setIsPayDisabled,
    bankTermsAndConditions,
    isBankTermsAgreed,
    setSplitAmount,
    onCheckInstantRefund,
    onInstantRefund,
    onCheckInstallmentPlans,
    onSelectedInstallmentPlanChange,
    hideInstallmentPlansUI,
    isDisabled
  } = props;
  const { t } = useTranslation('card');
  const { t: tPaymentLayout } = useTranslation('flash', { keyPrefix: 'paymentLayout' });
  const { checkBinFees, feesAmount, subType } = useSafeSelector((state: any) => state.payment);
  const intentionData = useSafeSelector((state: any) => state.intention);
  const hasCampaignDiscount = useSafeSelector((state: any) => state.discounts.hasCampaignDiscount);
  const { showInstantRefund } = useSafeSelector((state: any) => state.paymentDetails);
  const { fastCheckoutAccount, isFastCheckoutView, isConsentChecked } = useSafeSelector((state: any) => state.fastCheckout);
  const canUseFastCheckout = fastCheckoutAccount?.canUseFastCheckout;
  const lang = useSafeSelector((state: any) => state.lang);
  const langDirection = lang === 'ar' ? 'rtl' : 'ltr';
  const isRTL = langDirection === 'rtl';
  const {
    amount: checkoutAmount,
    displayCurrency,
    currencyClassName,
    currencyFontFamily,
  } = useCheckoutDisplayAmount('card');
  const [installmentContext, setInstallmentContext] = useState<InstallmentContext>({
    hasSelectedPlan: false,
    selectedPlanData: null,
    hasLoadedPlans: false,
  });
  const { hasSelectedPlan: isInstallmentPlanSelected, selectedPlanData, hasLoadedPlans } = installmentContext;

  const styling = {
    container: customStyle?.container,
    input: { ...customStyle?.input, direction: langDirection },
    button: customStyle?.button,
    label: customStyle?.label,
    error: customStyle?.error,
  };

  const {
    isSplitPaymentsView,
    currentCardNumber,
    splitCardCount,
    SplitPaymentButton,
  } = splitPayments || {};

  const isLastCard = currentCardNumber === splitCardCount;

  const {
    handleSubmit,
    errors,
    isDirty,
    isValid,
    cardNumberRegister,
    expiryRegister,
    cvcRegister,
    nameRegister,
    saveCardRegister,
    getValues,
    cardType,
    amountRegister,
    setValue,
    clearErrors,
    unregister
  } = useHookForm({
    ...(isSplitPaymentsView && { isAmount: true, maxAmount: amount }),
    isStandardCard: true,
    hasOmanNetIntegration,
    ...(isSplitPaymentsView && isLastCard && { defaultValues: { amount } }),
  });
  const cardNum = getValues('number');
  const cardBin = (cardNum?.replace(/\s/g, '') ?? '').slice(0, INSTALLMENT_BIN_LENGTH);
  const isValidCard = valid.number(cardNum).isValid;
  const isValidVisa = isValidCard && cardType?.toUpperCase() === SAVED_CARD_TYPES.VISA;
  const cardExpiry = getValues('expiry');
  const isValidExpiry = valid.expirationDate(cardExpiry).isValid;

  useEffect(() => {
    if (isValidVisa) setIsValidVisa?.(true);
    else setIsValidVisa?.(false);
  }, [isValidVisa]);

  useEffect(() => {
    setIsOmanNetCard?.(cardType === CARD_TYPES.OMAN || cardType === CARD_TYPES.MAAL);
  }, [cardType, setIsOmanNetCard]);

useEffect(() => {
    const hasFeesAmount = !!(feesAmount || subType?.feesAmount);
    const cardData = {
      cardNum,
      isValidCard,
      cardType,
      ...(hasCampaignDiscount && { cardExpiry, isValidExpiry }),
      hasFeesAmount
    };

    if (!checkBinFees && !hasFeesAmount) {
      if (showInstantRefund) {
        onCheckInstantRefund?.(cardData);
      } else {
        onDiscount?.(cardData);
      }
    } else if (checkBinFees || hasFeesAmount) {
      onFees?.(cardData);
    }
  }, [
    isValidCard,
    cardNum,
    cardType,
    hasCampaignDiscount && cardExpiry,
    hasCampaignDiscount && isValidExpiry,
    isSplitPaymentsView
  ]);

  useEffect(() => {
    setIsPayDisabled?.(!isValid);
  }, [isValid]);

  useEffect(() => {
     clearErrors();
     !isSplitPaymentsView && unregister('amount');
    if (isSplitPaymentsView && isLastCard) {
      setValue('amount', amount);
    }
  }, [isSplitPaymentsView]);

  const handleInstantRefundToggle = (isInstantRefundEnabled: boolean) => {
    onInstantRefund?.({
      isInstantRefundEnabled,
      cardNum,
      isValidCard,
      cardType,
      ...(hasCampaignDiscount && { cardExpiry, isValidExpiry }),
    });
  };

  const restAmount = intentionData.split_pay_details?.total_auth_amount_paid ?  formatAmountCentsBasedOnCurrency(
    intentionData.split_pay_details?.total_auth_amount_left || 0,
    currency
  ) : undefined

  const TermsAndConditionsURL = getTermsAndConditionsURL();

const cardContent = <>
  <p
        style={styling.label}
        className="text-sm font-[500] mb-2 text-gray-700"
      >
        {t('CARD_INFO')}
      </p>
      <CardNumber
        hideCardIcons={hideCardIcons}
        formRegister={cardNumberRegister}
        cardType={cardType}
        getCardLogo={getCardLogo}
        error={errors['number']}
        maxLength={
          cardType === CARD_TYPES.AMEX
            ? AMEX_CARD_LENGTH + CARD_NUMBER_SPACES
            : CARD_LENGTH + CARD_NUMBER_SPACES
        }
        customStyle={styling.input}
        className={`border-b-0 rounded-t-lg ${
          errors['number'] ? 'border-red-300' : 'border-gray-300'
        }`}
      />
      <div className="flex w-full">
        <ExpiryDate
          formRegister={expiryRegister}
          maxLength={EXPIRY_DATE_LENGTH}
          expiryDate={getValues('expiry')}
          customStyle={styling.input}
          error={errors['expiry']}
          className={`${isRTL ? "rounded-br-lg" : "rounded-bl-lg" } ${
            errors['number'] ? 'border-t-red-300' : ''
          } ${errors['cvc'] ? isRTL ? 'border-l-red-300' : 'border-r-red-300' : ''}
              ${errors['expiry'] ? 'border-red-300' : 'border-gray-300'}`}
        />
        <CVV
          formRegister={cvcRegister}
          maxLength={
            cardType === CARD_TYPES.AMEX ? AMEX_CVV_LENGTH : CVV_LENGTH
          }
          error={errors['cvc']}
          customStyle={styling.input}
          className={`${isRTL ? "border-r-0 rounded-bl-lg" : "border-l-0 rounded-br-lg" } ${
            errors['number'] ? 'border-t-red-500' : ''
          }
            ${errors['cvc'] ? 'border-red-300' : 'border-gray-300'}`}
        />
      </div>
      {isInstallmentPlanSelected &&
        (discountAppliedMessage || discountErrorMessage) &&
        !isSplitPaymentsView && (
          <div className="mt-2 flex items-start gap-2 text-gray-600 text-sm leading-[1.125rem] track-[0.01rem]">
            <img src={InfoIcon} alt="info" className="mt-[2px] h-4 w-4" />
            <p>{t('DISCOUNT_NOT_APPLICABLE_FOR_INSTALLMENTS')}</p>
          </div>
        )}
      {!isInstallmentPlanSelected && discountErrorMessage && (
        <p className="mt-2 text-orange-700 text-sm leading-[1.125rem] track-[0.01rem]">
          {discountErrorMessage}
        </p>
      )}
      {!isInstallmentPlanSelected && discountAppliedMessage && !isSplitPaymentsView && !canUseFastCheckout && (
        <p className="mt-2 text-green-400 text-sm leading-[1.125rem] track-[0.01rem]">
          {discountAppliedMessage}
        </p>
      )}
      {(errors['number'] || errors['cvc'] || errors['expiry']) && (
        <ul
          style={styling.error}
          className="my-2 text-xs text-red-600 list-disc list-inside list-item:text-red-600"
        >
          {errors['number'] && <li>{errors['number'].message}</li>}
          {errors['expiry'] && <li>{errors['expiry'].message}</li>}
          {errors['cvc'] && <li>{errors['cvc'].message}</li>}
        </ul>
      )}
      <p
        style={styling.label}
        className="text-sm font-[500] mt-4 mb-2 text-gray-700"
      >
        {t('CARDHOLDER_NAME')}
      </p>
      <div className="w-full overflow-hidden">
        <CardHolder
          formRegister={nameRegister}
          maxLength={CARD_HOLDER_NAME_MAX_LENGTH}
          error={errors['name']}
          customStyle={styling.input}
          className={`rounded-lg ${
            errors['name'] ? 'border-red-300' : 'border-gray-300'
          }`}
        />
      </div>
      {errors['name'] && (
        <p style={styling.error} className="my-2 text-xs text-red-600">
          {errors['name'].message}
        </p>
      )}

    <CardToInstallment
        mode="bin"
        cardBin={cardBin}
        hideUI={!!hideInstallmentPlansUI}
        onCheckInstallmentPlans={onCheckInstallmentPlans}
        onSelectedInstallmentPlanChange={onSelectedInstallmentPlanChange}
        onInstallmentContextChange={setInstallmentContext}
        displayCurrency={displayCurrency}
        intentionCurrency={intentionData.currency}
        planAmountCurrency={currency}
        tPaymentLayout={tPaymentLayout}
      />
      {forceSaveCard && !isFastCheckoutView  && !isConsentChecked && (
        <p className="w-full mt-4 text-sm text-gray-700">
          {t('FORCE_SAVE_CARD')}
        </p>
      )}
      {!forceSaveCard && showSaveCard && !isConsentChecked && !isFastCheckoutView && (
        <div className="w-full mt-4">
          <FormCheckbox
            formRegister={saveCardRegister}
            label={t('SAVE_CARD')}
            name="save_card"
            customStyle={styling.input}
          />
        </div>
      )}
      {content}
      {isFastCheckoutView && (
            <p className="w-full mt-4 text-sm text-gray-700">
              <Trans
                i18nKey="ONE_CLICK_HINT_TERMS"
                t={t}
                components={{
                  1: (
                    <a
                      href={TermsAndConditionsURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-900 underline underline-offset-2"
                    />
                  ),
                }}
              />
            </p>
          )}
      {showInstantRefund && !isSplitPaymentsView && !canUseFastCheckout && (
        <InstantRefundOpt
          onClick={handleInstantRefundToggle}
          isLoading={loading}
          forceDisabled={isInstallmentPlanSelected}
          forceDisabledMessage={t('INSTANT_REFUND_NOT_AVAILABLE_FOR_INSTALLMENTS')}
        />
      )}
        {SplitPaymentButton && (
          <div className="mt-4">
            <SplitPaymentButton />
          </div>
        )}
        {isSplitPaymentsView && (
          <div className="mt-4">
            <p className="mb-2 text-gray-700 english-label-medium">
              {isLastCard ? t('REMAINING_AMOUNT') :t('ENTER_AMOUNT')}
            </p>
            <InputViewTwo
              type="number"
              name="amount"
              label={
                <div className="absolute flex items-center h-full ">
                  <p className="text-sm leading-4 text-gray-900 font-Inter">
                    <span style={{fontFamily:currencyFontFamily}}>{tPaymentLayout(displayCurrency)}</span>
                  </p>
                </div>
              }
              placeholder="0.0"
              customStyle={{ gap: '1rem', borderColor: errors['amount'] ? '#F87171' : '' , backgroundColor: isLastCard ? '#F9FAFB' : 'white',
              color: isLastCard ? "#667085": ""}}
              disabled={isLastCard}
              max={amount}
              value={getValues().amount}
              {...amountRegister}
              onChange={(e: any) => {
                amountRegister.onChange(e);
                setSplitAmount?.(e.target.value);
              }}
            />
            {restAmount && !isLastCard && <p className='font-inter text-sm leading-[18px] text-gray-600 mt-2'>{t('REMAINING_AMOUNT')} :
              <span className={currencyClassName}>{tPaymentLayout(displayCurrency)}</span>{' '} {restAmount}</p>}
            </div>
        )}
        {errors['amount']  && (
          <ul className="my-2 text-xs text-red-600 list-disc list-inside list-item:text-red-600">
            {errors['amount'] && <li>{errors['amount'].message}</li>}
          </ul>
        )}</>
  const handleFormSubmit = (data: any) => {
    onSubmit({
      ...data,
      ...(selectedPlanData && { tenure: String(selectedPlanData.months) }),
    });
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      noValidate
      className="flex flex-col w-full h-full max-w-full m-auto"
      style={styling.container}
    >
      {CardPaymentWrapper ? <CardPaymentWrapper>
        {cardContent}
      </CardPaymentWrapper>: cardContent}
      {children ? (
        Children.map(children, (child) => {
          if (!isValidElement(child)) return null;
          return cloneElement(child, {
            ...child.props,
            isDirty,
            isValid,
          });
        })
      ) : (
        <div className="w-full mt-auto">
          {showTerms && !isFastCheckoutView && (
            <TermsConditions/>
          )}
          {bankTermsAndConditions}
          <Button
            label={isSplitPaymentsView ? (
              buttonLabel || getValues('amount') ?
              <>
                  {t('CARD_ACTION_BTN') + " "}
                  <span className={currencyClassName}>{tPaymentLayout(displayCurrency)}</span>{' '}
                  {getValues('amount')}
              </> : t('CARD_ACTION_BTN')) :
              selectedPlanData ? (
              <>
                {buttonLabel || t('CARD_ACTION_BTN')}{' '}
                <span className={currencyClassName}>{tPaymentLayout(displayCurrency)}</span>{' '}
                {formatAmountCentsBasedOnCurrency(
                  selectedPlanData.amount,
                  currency
                )}
                {t('PAY_PER_MONTH')}
              </>
              ) : hasLoadedPlans ? (
              <>
                {buttonLabel || t('INSTALLMENT_PAY_IN_FULL')}{' '}
                <span className={currencyClassName}>{tPaymentLayout(displayCurrency)}</span>{' '}
                {checkoutAmount}
              </>
              ) : (
              <>
                {buttonLabel || t('CARD_ACTION_BTN')}{' '}
                  {displayCurrency && (
                    <span className={currencyClassName}>
                      {tPaymentLayout(displayCurrency)}{' '}
                    </span>
                  )}
                {checkoutAmount}
              </>
              )
            }
            id="pay-button"
            loading={loading}
            fullWidth={true}
            disabled={isDisabled || loading || !isDirty || !isValid || (bankTermsAndConditions && !isBankTermsAgreed)}
            customStyle={styling.button}
          />
        </div>
      )}
    </form>
  );
}

export default Card;
